/**
 * Payment validators - Payment, billing, and invoice related types
 */
import { Infer, v } from 'convex/values';

// ============================================================================
// Payment Status
// ============================================================================

export const paymentStatusValidator = v.union(
  v.literal('pending'),
  v.literal('succeeded'),
  v.literal('failed'),
  v.literal('expired'),
  v.literal('canceled'),
  v.literal('refunded')
);

export type PaymentStatus = Infer<typeof paymentStatusValidator>;

export const PAYMENT_STATUSES = [
  'pending',
  'succeeded',
  'failed',
  'expired',
  'canceled',
  'refunded',
] as const;

// ============================================================================
// Payment Type
// ============================================================================

export const paymentTypeValidator = v.union(
  v.literal('provider_platform_fee'),
  v.literal('substitute_platform_fee'),
  v.literal('invoice_payment'),
  v.literal('work_payment'),
  v.literal('initial_system_fee'),
  v.literal('visibility_upsell'),
  v.literal('email_blast_upsell'),
  v.literal('fast_lane_fee')
);

export type PaymentType = Infer<typeof paymentTypeValidator>;

export const PAYMENT_TYPES = [
  'provider_platform_fee',
  'substitute_platform_fee',
  'invoice_payment',
  'work_payment',
  'initial_system_fee',
  'visibility_upsell',
  'email_blast_upsell',
  'fast_lane_fee',
] as const;

// ============================================================================
// Provider Platform Fee Collection
// ============================================================================

export const platformFeeCollectionModeValidator = v.union(
  v.literal('checkout'),
  v.literal('sepa_offsession'),
  v.literal('invoice_transfer')
);

export type PlatformFeeCollectionMode = Infer<typeof platformFeeCollectionModeValidator>;

// ============================================================================
// Work Invoice Status
// ============================================================================

export const workInvoiceStatusValidator = v.union(
  v.literal('draft'),
  v.literal('issued'),
  v.literal('paid'),
  v.literal('superseded')
);

export type WorkInvoiceStatus = Infer<typeof workInvoiceStatusValidator>;

export const WORK_INVOICE_STATUSES = ['draft', 'issued', 'paid', 'superseded'] as const;

/**
 * Payment methods exposed by role-facing work-invoice reads.
 *
 * Persisted user/payment records intentionally remain limited to the two
 * known methods above. Reads normalize missing or legacy values to
 * `unknown` so callers cannot accidentally enter a live payment branch.
 */
export const workInvoicePaymentMethodReadValidator = v.union(
  v.literal('mollie_connect'),
  v.literal('bank_transfer'),
  v.literal('unknown')
);

export type WorkInvoicePaymentMethod = Infer<typeof workInvoicePaymentMethodReadValidator>;

export const normalizeWorkInvoicePaymentMethod = (paymentMethod: string | undefined): WorkInvoicePaymentMethod => {
  if (paymentMethod === 'mollie_connect' || paymentMethod === 'bank_transfer') return paymentMethod;
  return 'unknown';
};
